import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { StatusBadge } from "@/components/Badges";
import { Plus, FolderOpen, FileText, Clock, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";

const PROBLEMS = [
  ["faulty_product", "Faulty or misdescribed product"], ["poor_service", "Poor or missing service"],
  ["refund", "Refund refused or delayed"], ["cancellation", "Cancellation"],
  ["subscription", "Subscription or recurring payment"], ["travel", "Travel, ticket or accommodation"],
  ["chargeback", "Chargeback or Section 75"], ["unsure", "Unsure / triage"],
];

export default function Dashboard() {
  const nav = useNavigate();
  const [matters, setMatters] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [ptype, setPtype] = useState("faulty_product");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/matters").then(({ data }) => setMatters(data));
  useEffect(() => { load(); }, []);

  const removeMatter = async (id) => {
    try { await api.delete(`/matters/${id}`); toast.success("Matter deleted"); load(); }
    catch { toast.error("Could not delete matter"); }
  };

  const create = async (goIntake) => {
    setBusy(true);
    try {
      const { data } = await api.post("/matters", { problem_type: ptype, client_name: name || "New client" });
      toast.success("Matter created");
      nav(goIntake ? `/intake/${data.id}` : `/matter/${data.id}`);
    } catch { toast.error("Could not create matter"); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Nav />
      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">Claims dashboard</div>
            <h1 className="font-serif text-4xl font-semibold tracking-tight text-slate-900">Matters</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-matter-btn" className="rounded-full bg-green-800 hover:bg-green-900"><Plus className="mr-1.5 h-4 w-4" /> New matter</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif text-2xl">Create a new matter</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Client name</label>
                  <Input data-testid="new-matter-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Doe" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Problem type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {PROBLEMS.map(([k, l]) => (
                      <button key={k} data-testid={`ptype-${k}`} onClick={() => setPtype(k)}
                        className={`rounded-lg border p-2.5 text-left text-sm font-medium transition-colors ${ptype === k ? "border-green-800 bg-green-50 text-green-900" : "border-slate-200 hover:border-slate-400"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button data-testid="create-goto-intake" disabled={busy} onClick={() => create(true)} className="flex-1 rounded-full bg-green-800 hover:bg-green-900">Create & start intake</Button>
                  <Button data-testid="create-goto-matter" disabled={busy} variant="outline" onClick={() => create(false)} className="rounded-full">Create only</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-8 grid gap-4">
          {matters.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-16 text-center">
              <FolderOpen className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 text-slate-500">No matters yet. Create your first matter to begin.</p>
            </div>
          )}
          {matters.map((m) => (
            <div key={m.id} data-testid={`matter-row-${m.id}`}
              className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-slate-400 hover:shadow-sm">
              <button onClick={() => nav(`/matter/${m.id}`)} className="min-w-0 flex-1 text-left">
                <div className="flex items-center gap-3">
                  <h3 className="truncate text-lg font-medium text-slate-900">{m.title}</h3>
                  <StatusBadge status={m.status} />
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-4 text-sm text-slate-500">
                  <span>{m.problem_label}</span>
                  <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {m.doc_count} docs</span>
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {new Date(m.created_at).toLocaleDateString()}</span>
                  {!m.intake_complete && <span className="text-amber-600">Intake in progress</span>}
                </div>
              </button>
              <div className="flex items-center gap-3 pl-4">
                <span className="hidden text-sm font-medium text-green-800 group-hover:inline">Open →</span>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-matter-${m.id}`} onClick={(e) => e.stopPropagation()} className="text-slate-300 transition-colors hover:text-red-500" title="Delete matter">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this matter?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently deletes “{m.title}” and all its documents, drafts, research and history. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid={`confirm-delete-${m.id}`} className="bg-red-600 hover:bg-red-700" onClick={() => removeMatter(m.id)}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
